// Zero reports (Informes en cero): for every closed calendar month in which no
// notice was due, the company must still file a report saying so, with the
// same deadline as a notice (day 17 of the following month).
import { MAX_ZERO_REPORT_MONTHS } from "./constants";
import { addMonthsToMonth, dateOfTimestamp, monthOf } from "./dates";
import { type DeadlineStatus, deadlineStatus, zeroReportDeadline } from "./deadlines";
import type { NoticeObligation } from "./notices";
import type { IsoDate, IsoMonth, Operation, ZeroReportRecord } from "./types";

export interface ZeroReport {
  month: IsoMonth;
  deadline: IsoDate;
  /** False when a notice was due that month: the notice replaces the zero report. */
  required: boolean;
  record: ZeroReportRecord | null;
  status: DeadlineStatus | "not_required";
}

/**
 * First month the portal tracks for a company: the month it was registered,
 * or earlier if historical operations were captured.
 */
export function zeroReportStartMonth(
  companyCreatedAt: string,
  operations: readonly Pick<Operation, "date">[],
): IsoMonth {
  let start = monthOf(dateOfTimestamp(companyCreatedAt));
  for (const op of operations) {
    const month = monthOf(op.date);
    if (month < start) start = month;
  }
  return start;
}

/** Every closed month since `startMonth`, most recent first. */
export function computeZeroReports(input: {
  startMonth: IsoMonth;
  today: IsoDate;
  notices: readonly Pick<NoticeObligation, "month">[];
  filed: Readonly<Record<IsoMonth, ZeroReportRecord>>;
}): ZeroReport[] {
  const currentMonth = monthOf(input.today);
  const monthsWithNotice = new Set(input.notices.map((n) => n.month));
  const earliest = addMonthsToMonth(currentMonth, -MAX_ZERO_REPORT_MONTHS);

  const reports: ZeroReport[] = [];
  for (
    let month = input.startMonth < earliest ? earliest : input.startMonth;
    month < currentMonth;
    month = addMonthsToMonth(month, 1)
  ) {
    const required = !monthsWithNotice.has(month);
    const record = input.filed[month] ?? null;
    const deadline = zeroReportDeadline(month);
    reports.push({
      month,
      deadline,
      required,
      record,
      status: required ? deadlineStatus(deadline, input.today, record !== null) : "not_required",
    });
  }
  return reports.reverse();
}

export const pendingZeroReports = (reports: readonly ZeroReport[]) =>
  reports.filter((r) => r.required && r.record === null);
